import React, { useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Eye, EyeOff, Home, KeyRound, ListChecks, LogOut, Truck, Undo2, X } from "lucide-react";
import { EkycChatButton } from "@/components/EkycChatButton";
import { HEAD, shortId } from "@/components/PdPill";

const TITLES = { "": "Delivery", pending: "Pending", complaint: "Complaint", done: "Done", logins: "Agency Logins" };
const iconBtn = "grid place-items-center w-10 h-10 rounded-full hover:bg-white/10 active:bg-white/20 transition-colors";

export function DmHeader({ hide, setHide, logout }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const seg = pathname.replace(/^\/delivery\/?/, "").split("/")[0];
  const back = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/delivery"));
  return (
    <header className="sticky top-0 z-40 bg-[#385C4B] text-white shadow-[0_6px_20px_rgba(56,92,75,0.25)]" data-testid="dm-header">
      <div className="max-w-xl mx-auto h-14 px-2 flex items-center gap-0.5">
        {seg ? (
          <>
            <button onClick={back} className={iconBtn} data-testid="dm-back" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
            <button onClick={() => navigate("/delivery")} className={iconBtn} data-testid="dm-home-btn" aria-label="Home"><Home className="w-5 h-5" /></button>
          </>
        ) : (
          <span className="grid place-items-center w-10 h-10"><Truck className="w-5 h-5" /></span>
        )}
        <h1 className={`${HEAD} text-lg font-semibold ml-1`} data-testid="dm-title">{TITLES[seg] || "Delivery"}</h1>
        <span className="ml-auto mr-1 flex items-center gap-1.5 text-[10px] font-mono tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />LIVE
        </span>
        <EkycChatButton className="hover:bg-white/10 [&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline" />
        <button onClick={() => setHide((v) => !v)} className={iconBtn} data-testid="dm-toggle-ids" title={hide ? "Show IDs" : "Hide IDs"} aria-label={hide ? "Show IDs" : "Hide IDs"}>
          {hide ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
        <button onClick={logout} className={iconBtn} data-testid="dm-logout" aria-label="Logout"><LogOut className="w-5 h-5" /></button>
      </div>
    </header>
  );
}

const NAV = [
  ["", "Home", Home, null, ""],
  ["pending", "Pending", ListChecks, "pending", "bg-[#8A6046]"],
  ["complaint", "Complaint", AlertTriangle, "complaint", "bg-[#D9534F]"],
  ["done", "Done", CheckCircle2, "done_today", "bg-[#385C4B]"],
  ["logins", "Logins", KeyRound, null, ""],
];

export function DmBottomNav({ t }) {
  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E5E5DF] shadow-[0_-8px_30px_rgba(0,0,0,0.06)] pb-[env(safe-area-inset-bottom)]" data-testid="dm-bottom-nav">
      <div className="max-w-xl mx-auto grid grid-cols-5 h-16">
        {NAV.map(([path, label, Icon, key, badge]) => (
          <NavLink key={label} to={path ? `/delivery/${path}` : "/delivery"} end={!path} data-testid={`dm-nav-${label.toLowerCase()}`}
            className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors ${isActive ? "text-[#385C4B]" : "text-[#8A8A80]"}`}>
            {({ isActive }) => (
              <>
                <span className={`absolute top-0 h-[3px] w-10 rounded-b-full ${isActive ? "bg-[#385C4B]" : "bg-transparent"}`} />
                <span className={`relative grid place-items-center w-12 h-7 rounded-full transition-colors ${isActive ? "bg-[#385C4B]/10" : ""}`}>
                  <Icon className="w-5 h-5" />
                  {key && t?.[key] > 0 && (
                    <span className={`absolute -top-1.5 -right-1 min-w-[18px] h-[18px] px-1 rounded-full ${badge} text-white text-[10px] font-mono leading-[18px] text-center`} data-testid={`dm-nav-badge-${label.toLowerCase()}`}>
                      {t[key] > 999 ? "999+" : t[key]}
                    </span>
                  )}
                </span>
                {label}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

const ST = { correct: "Done", wrong: "DAC ✗", cancelled: "Cancelled" };

export function UndoToast({ last, hide, onUndo, onClose }) {
  useEffect(() => {
    if (!last) return undefined;
    const t = setTimeout(onClose, 10000);
    return () => clearTimeout(t);
  }, [last, onClose]);
  if (!last) return null;
  return (
    <div className="fixed inset-x-3 bottom-[calc(76px+env(safe-area-inset-bottom))] z-50 mx-auto max-w-xl rounded-xl bg-[#1A1A1A]/95 backdrop-blur-md text-white pl-4 pr-2 py-2.5 flex items-center gap-3 shadow-2xl border border-white/10 animate-in fade-in slide-in-from-bottom-4" data-testid="dm-undo-toast">
      <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0" />
      <span className="text-sm min-w-0 truncate" data-testid="dm-undo-text">
        <span className="font-mono">{hide ? "••••…••••" : shortId(last.r.lpg_id)}</span> · {ST[last.status]}
      </span>
      <button onClick={() => onUndo(last.r)} className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-[#FFD9A8] text-sm font-bold transition-colors" data-testid="dm-undo-btn">
        <Undo2 className="w-4 h-4" />Undo
      </button>
      <button onClick={onClose} className="grid place-items-center w-8 h-8 rounded-full hover:bg-white/10 transition-colors" data-testid="dm-undo-close" aria-label="Close">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
